import { Injectable } from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';
import { AuthenService } from '@core/authen/authen.service';
import { SessionService } from '@core/session.service';
import { AttributeDescription } from '@shared/interfaces/attribute-description.interface';
import { EmailValidator } from '@shared/utils/form-fields-validation';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
} from 'rxjs/operators';

export const EMAIL_ATTRIBUTE_PROPERTY_NAME = 'EmailAddress';

@Injectable({
  providedIn: 'root',
})
export class EmailRegexService {
  private emailRegex$: Observable<string | null> | null = null;

  private latestEmailValidator: ValidatorFn | null = null;

  constructor(
    private readonly authenService: AuthenService,
    private readonly sessionService: SessionService
  ) {}

  getEmailRegex(): Observable<string | null> {
    this.emailRegex$ ??= this.sessionService.apiReady$.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.authenService.getUserAttribute()),
      map(attributes => this.extractEmailRegex(attributes)),
      catchError(() => of(null)),
      shareReplay(1)
    );

    return this.emailRegex$;
  }

  emailValidator(): ValidatorFn {
    if (!this.latestEmailValidator) {
      this.latestEmailValidator = Validators.email;

      this.getEmailRegex()
        .pipe(take(1))
        .subscribe(regex => {
          if (regex) {
            this.latestEmailValidator = EmailValidator.pattern(regex);
          }
        });
    }

    return control => this.latestEmailValidator(control);
  }

  private extractEmailRegex(attributes: AttributeDescription[]): string | null {
    const emailAttribute = attributes?.find(
      attribute => attribute.propertyName === EMAIL_ATTRIBUTE_PROPERTY_NAME
    );

    return emailAttribute?.regularExpression?.trim()
      ? emailAttribute.regularExpression
      : null;
  }
}
